/**
 * Clinic OS — single source of truth (v3).
 * Every clinic runs on Clinic Hub OR Clinic Hub+, then snaps on add-ons.
 */
import {
  LayoutGrid, ShieldCheck, PhoneCall, Bot, BarChart3, Megaphone, GraduationCap, Target,
} from "lucide-react";

/* ---------- BASES (one required) ---------- */
export const BASES = [
  {
    id: "hub",
    name: "Clinic Hub",
    short: "Hub",
    tag: "Base",
    icon: LayoutGrid,
    color: "from-blue-500 to-blue-600",
    accent: "text-blue-500",
    ring: "ring-blue-500/30",
    bg: "bg-blue-500/10",
    tagline: "Get organized — one inbox, one calendar, one EMR.",
    price: 297,
    priceLabel: "$297",
    period: "per month",
    badge: null,
    features: [
      "Unified Inbox (calls, SMS, email, social)",
      "Scheduling & Calendar",
      "EMR Bridge — 2-way sync",
      "Forms & Surveys",
      "Desk phone (Yealink/SIP) ready",
    ],
    replaces: ["JotForm", "Call Hero", "Zapier for EMR", "Patient spreadsheets"],
  },
  {
    id: "hub-plus",
    name: "Clinic Hub+",
    short: "Hub+",
    tag: "Base · Most popular",
    icon: ShieldCheck,
    color: "from-violet-500 to-fuchsia-500",
    accent: "text-violet-500",
    ring: "ring-violet-500/30",
    bg: "bg-violet-500/10",
    tagline: "Run the whole clinic — automate, post, and capture every call.",
    price: 497,
    priceLabel: "$497",
    period: "per month",
    badge: "Most popular",
    features: [
      "Everything in Clinic Hub",
      "Social Suite (schedule + reply across channels)",
      "Automations (workflows, follow-ups, reminders)",
      "Call IQ Essential (tracking, recording, transcripts)",
    ],
    replaces: ["Hootsuite", "Zapier", "Call tracking tools"],
  },
];

export const HUB = BASES[0];
export const HUB_PLUS = BASES[1];

/* ---------- ADD-ONS (snap on top of Hub or Hub+) ---------- */
export const ADDONS = [
  {
    id: "call-iq-ai",
    name: "Call IQ AI",
    tag: "Add-on",
    icon: PhoneCall,
    color: "from-sky-500 to-cyan-500",
    accent: "text-sky-500",
    ring: "ring-sky-500/30",
    bg: "bg-sky-500/10",
    requiresBase: "hub-plus",
    tagline: "Hear what every patient really means.",
    features: [
      "AI call classification & intent",
      "Booking status detection",
      "Patient identification",
      "Auto call summaries",
    ],
    replaces: ["Call Hero", "Retell AI", "Manual call review"],
  },
  {
    id: "ai-workforce",
    name: "AI Workforce",
    tag: "Add-on",
    icon: Bot,
    color: "from-violet-500 to-fuchsia-500",
    accent: "text-violet-500",
    ring: "ring-violet-500/30",
    bg: "bg-violet-500/10",
    requiresBase: "hub-plus",
    tagline: "AI staff that never sleep.",
    features: [
      "AI Receptionist (answers after 3 rings)",
      "Copilot (cross-channel replies + reactivation)",
      "Ask Mathew (SOP articles + chatbot)",
      "Review AI (auto-respond to Google & Facebook reviews)",
    ],
    replaces: ["Call Hero", "Retell AI", "Internal wikis"],
  },
  {
    id: "clinic-pulse",
    name: "Clinic Pulse",
    tag: "Add-on",
    icon: BarChart3,
    color: "from-emerald-500 to-teal-500",
    accent: "text-emerald-500",
    ring: "ring-emerald-500/30",
    bg: "bg-emerald-500/10",
    requiresBase: null,
    tagline: "EMR + CRM insights, one dashboard.",
    features: [
      "EMR + CRM stat dashboards",
      "Patient, revenue & funnel reports",
      "Pulse reports — weekly clinic health",
      "Conversation intelligence rollups",
    ],
    replaces: ["Tableau", "Custom EMR exports", "Spreadsheets"],
  },
  {
    id: "growth",
    name: "Growth",
    tag: "Add-on",
    icon: Megaphone,
    color: "from-orange-500 to-rose-500",
    accent: "text-orange-500",
    ring: "ring-orange-500/30",
    bg: "bg-orange-500/10",
    requiresBase: "hub-plus",
    tagline: "Fill your funnel on autopilot.",
    features: [
      "Ads, funnels & landing pages",
      "Reactivation campaigns",
      "Reviews & reputation",
      "SMS / Email broadcasts",
    ],
    replaces: ["HubSpot", "SharpSpring", "GradeUs"],
  },
  {
    id: "academy",
    name: "Academy",
    tag: "Add-on",
    icon: GraduationCap,
    color: "from-amber-500 to-yellow-500",
    accent: "text-amber-500",
    ring: "ring-amber-500/30",
    bg: "bg-amber-500/10",
    requiresBase: null,
    tagline: "Educate patients, build a community.",
    features: [
      "Patient education courses",
      "Member community",
      "Drip lessons & quizzes",
      "Branded learner experience",
    ],
    replaces: ["Kajabi", "Skool"],
  },
  {
    id: "sales-autopilot",
    name: "Sales Autopilot",
    tag: "Add-on",
    icon: Target,
    color: "from-rose-500 to-pink-500",
    accent: "text-rose-500",
    ring: "ring-rose-500/30",
    bg: "bg-rose-500/10",
    requiresBase: "hub-plus",
    tagline: "Close more leads — on autopilot.",
    features: [
      "Stale lead check & re-engage",
      "Qualify intent automatically",
      "Increase lead-to-close average",
      "Improve funnel conversion",
    ],
    replaces: ["Manual lead chasing", "Salesforce Cadence", "Outreach"],
  },
];

/* Combined list for the Products dropdown / sitemap. */
export const MODULES = [...BASES, ...ADDONS];

/* ---------- SOLUTIONS (tailored bundles) ---------- */
export const SOLUTIONS = [
  {
    id: "core",
    name: "Core OS",
    tagline: "Get organized.",
    description: "Built on Clinic Hub — everything a solo clinic needs to stop juggling tools and start running on one system.",
    baseId: "hub",
    addonIds: [],
    bestFor: "Solo clinics getting organized",
    priceLabel: "$297/mo",
    cta: "Book a Demo",
    featured: false,
    accent: "from-blue-500/20 to-blue-600/0",
    badge: "Starts here",
  },
  {
    id: "growth",
    name: "Growth OS",
    tagline: "Capture & convert more patients.",
    description: "Clinic Hub+ supercharged with AI Workforce and Growth — your front desk and marketing engine, fully automated.",
    baseId: "hub-plus",
    addonIds: ["ai-workforce", "growth"],
    bestFor: "Clinics ready to grow without hiring",
    priceLabel: "Book a Demo",
    cta: "Book a Demo",
    featured: true,
    accent: "from-violet-500/30 to-fuchsia-500/0",
    badge: "Most popular",
  },
  {
    id: "scale",
    name: "Scale OS",
    tagline: "Max automation, multi-location.",
    description: "The full Clinic OS — every module activated on top of Hub+ for max throughput and visibility.",
    baseId: "hub-plus",
    addonIds: ["call-iq-ai", "ai-workforce", "clinic-pulse", "growth", "academy"],
    bestFor: "Multi-location / max automation",
    priceLabel: "Book a Demo",
    cta: "Book a Demo",
    featured: false,
    accent: "from-emerald-500/20 to-teal-500/0",
    badge: "Most powerful",
  },
  {
    id: "enterprise",
    name: "Enterprise OS",
    tagline: "Custom-built for large groups.",
    description: "Full Clinic OS + custom integrations, dedicated success manager, advanced security & SLAs, custom Ask Mathew training.",
    baseId: "hub-plus",
    addonIds: ["call-iq-ai", "ai-workforce", "clinic-pulse", "growth", "academy"],
    bestFor: "Large multi-location groups",
    priceLabel: "Inquire",
    cta: "Talk to Sales",
    featured: false,
    accent: "from-zinc-500/20 to-zinc-700/0",
    badge: "Inquire",
    inquireOnly: true,
  },
];

export const baseById = (id) => BASES.find((b) => b.id === id);
export const addonById = (id) => ADDONS.find((a) => a.id === id);
export const moduleById = (id) => MODULES.find((m) => m.id === id);
